import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { AccessToken, DownloadJob, EventPhoto, PhotoEvent, PhotoEventWithPricelistAndPhotos, Pricelist } from 'common-models';
import { Configuration } from '../../Configuration';

export const eventsApiSlice = createApi({
  reducerPath: 'eventsApi',
  tagTypes: ['event', 'event-with-parents', 'eventphoto', 'eventwithphotos', 'events', 'pricelists'],

  baseQuery: fetchBaseQuery({
    baseUrl: Configuration.ApiUrl,
    credentials: 'include',
    prepareHeaders: (headers) => {
      return headers;
    },
  }),
  endpoints: (builder) => ({
    getAllEvents: builder.query<PhotoEvent[], void>({
      query: () => '/events',
      providesTags: [{ type: 'events', id: 'all-events' }],
    }),

    getSingleEvent: builder.query<PhotoEvent, { eventId: string }>({
      query: (input) => `/events/${input.eventId}`,
      providesTags: (result) => [{ type: 'event', id: result?.id }],
    }),

    getSingleEventWithParents: builder.query<Array<PhotoEvent>, { eventId: string }>({
      query: (input) => `/events/${input.eventId}/withparents`,
      providesTags: (result, error, input) => [{ type: 'event-with-parents', id: input.eventId }],
    }),

    getSubEvents: builder.query<PhotoEvent[], { eventId: string }>({
      query: (input) => `/events/${input.eventId}/subevents`,
      providesTags: (result, error, request) => [{ type: 'events', id: `sub-events-${request.eventId}` }],
    }),

    getSingleEventWithPhotos: builder.query<PhotoEventWithPricelistAndPhotos, { eventId: string }>({
      query: (input) => `/events/${input.eventId}/full`,
      providesTags: (result) => [{ type: 'eventwithphotos', id: result?.event.id }],
    }),

    getSingleEventPhoto: builder.query<EventPhoto, { eventId: string; imageId: string }>({
      query: (input) => `/events/${input.eventId}/images/${input.imageId}`,
      providesTags: (result) => [{ type: 'eventphoto', id: result?.id }],
    }),

    getPhotoDownloadUrl: builder.query<string, { eventId: string; imageId: string }>({
      query: (input) => `/events/${input.eventId}/images/${input.imageId}/download`,
    }),

    getDownloadJob: builder.query<DownloadJob, { jobId: string }>({
      query: (input) => `/downloads/${input.jobId}`,
    }),

    downloadPhotosZip: builder.mutation<DownloadJob, string[]>({
      query: (imagesToDownload) => ({
        url: '/downloads/zip',
        method: 'POST',
        body: imagesToDownload,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    }),

    // ----- Admin API's below this line -----

    getAllEventsForAdmin: builder.query<PhotoEvent[], AccessToken>({
      query: (accessToken) => ({
        url: '/admin/events',
        method: 'GET',
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }),
      providesTags: [{ type: 'events', id: 'all-events' }],
    }),

    getAllPricelists: builder.query<Pricelist[], AccessToken>({
      query: (accessToken) => ({
        url: '/admin/pricelists',
        method: 'GET',
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }),
      providesTags: [{ type: 'pricelists', id: 'all-pricelists' }],
    }),

    storeSingleEvent: builder.mutation<any, [PhotoEvent, AccessToken]>({
      query: ([photoEvent, accessToken]) => ({
        url: '/admin/events',
        method: 'POST',
        body: photoEvent,
        headers: {
          'Content-type': 'application/json; charset=UTF-8',
          Authorization: `Bearer ${accessToken}`,
        },
      }),
      invalidatesTags: ['events', 'eventwithphotos', 'event-with-parents'],
    }),

    setEventFrontImage: builder.mutation<any, { eventId: string; photoId: string; accessToken: AccessToken }>({
      query: (input) => ({
        url: `/admin/events/${input.eventId}/frontimage/${input.photoId}`,
        method: 'PUT',
        headers: {
          'Content-type': 'application/json; charset=UTF-8',
          Authorization: `Bearer ${input.accessToken}`,
        },
      }),
      invalidatesTags: ['event', 'events', 'eventwithphotos'],
    }),

    deleteSingleEventPhoto: builder.mutation<any, { eventId: string; photoId: string; accessToken: AccessToken }>({
      query: (input) => ({
        url: `/admin/events/${input.eventId}/images/${input.photoId}`,
        method: 'DELETE',
        headers: {
          'Content-type': 'application/json; charset=UTF-8',
          Authorization: `Bearer ${input.accessToken}`,
        },
      }),
      invalidatesTags: ['event', 'events', 'eventwithphotos'],
    }),

    deleteSingleEvent: builder.mutation<any, [string, AccessToken]>({
      query: ([eventId, accessToken]) => ({
        url: `/admin/events/${eventId}`,
        method: 'DELETE',
        headers: {
          'Content-type': 'application/json; charset=UTF-8',
          Authorization: `Bearer ${accessToken}`,
        },
      }),
      invalidatesTags: ['event', 'events', 'eventwithphotos'],
    }),

    storePricelist: builder.mutation<any, [Pricelist, AccessToken]>({
      query: ([photoEvent, accessToken]) => ({
        url: '/admin/pricelists',
        method: 'POST',
        body: photoEvent,
        headers: {
          'Content-type': 'application/json; charset=UTF-8',
          Authorization: `Bearer ${accessToken}`,
        },
      }),
      invalidatesTags: ['pricelists'],
    }),
  }),
});

export const {
  useGetAllEventsQuery,
  useGetAllEventsForAdminQuery,
  useGetSingleEventQuery,
  useLazyGetSingleEventQuery,
  useGetSingleEventWithParentsQuery,
  useGetSubEventsQuery,
  useGetSingleEventWithPhotosQuery,
  useLazyGetSingleEventWithPhotosQuery,
  useGetAllPricelistsQuery,
  useGetSingleEventPhotoQuery,
  useLazyGetDownloadJobQuery,
  useLazyGetPhotoDownloadUrlQuery,
  useDownloadPhotosZipMutation,
  useStoreSingleEventMutation,
  useStorePricelistMutation,
  useSetEventFrontImageMutation,
  useDeleteSingleEventMutation,
  useDeleteSingleEventPhotoMutation,
} = eventsApiSlice;
